import {
  Award,
  Calendar,
  Download,
  Filter,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { statsService } from "../services/api";

const Statistiques = () => {
  const [stats, setStats] = useState(null);
  const [topParents, setTopParents] = useState([]);
  const [activitesPopulaires, setActivitesPopulaires] = useState([]);
  const [participationMensuelle, setParticipationMensuelle] = useState(null);
  const [participationParClasse, setParticipationParClasse] = useState([]);
  const [statsRecompenses, setStatsRecompenses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchAllStats();
  }, [selectedMonth, selectedYear]);

  const fetchAllStats = async () => {
    try {
      setLoading(true);

      const [
        statsResponse,
        topParentsResponse,
        activitesResponse,
        participationResponse,
        classesResponse,
        recompensesResponse,
      ] = await Promise.all([
        statsService.getGlobales(),
        statsService.getTopParents(10),
        statsService.getActivitesPopulaires(10),
        statsService.getParticipationMensuelle(selectedMonth, selectedYear),
        statsService.getParticipationParClasse(),
        statsService.getStatsRecompenses(),
      ]);

      setStats(statsResponse.data);
      setTopParents(topParentsResponse.data);
      setActivitesPopulaires(activitesResponse.data);
      setParticipationMensuelle(participationResponse.data);
      setParticipationParClasse(classesResponse.data);
      setStatsRecompenses(recompensesResponse.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await statsService.exportCSV();
      // Créer un fichier CSV et le télécharger
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `statistiques-etampon-${selectedYear}-${selectedMonth}.json`;
      link.click();
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Statistiques</h1>
            <p className="text-gray-600">Analyse de l'engagement des parents</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="input-field text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("fr", { month: "long" })}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="input-field text-sm"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <button
              onClick={handleExport}
              className="btn-primary flex items-center space-x-2"
            >
              <Download className="h-5 w-5" />
              <span>Exporter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Parents</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.total_parents || 0}
              </p>
              <p className="text-xs text-gray-500">
                {stats?.parents_actifs || 0} actifs
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-success-100 rounded-lg flex items-center justify-center">
                <Award className="h-6 w-6 text-success-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Tampons</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.total_tampons || 0}
              </p>
              <p className="text-xs text-gray-500">
                {stats?.total_points || 0} points
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-warning-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-warning-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Activités ce mois
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.activites_ce_mois || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Taux d'engagement
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.total_parents > 0
                  ? Math.round(
                      (stats?.parents_actifs / stats?.total_parents) * 100
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Parents */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Top 10 Parents les plus actifs
          </h2>
          <div className="space-y-3">
            {topParents.map((parent, index) => (
              <div
                key={parent.parent_id}
                className="flex items-center justify-between py-2"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-600">
                        #{index + 1}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-medium text-gray-900">
                      {parent.nom_parent}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {parent.tampons_total} tampons
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-success-600">
                    {parent.points_total} pts
                  </p>
                  <p className="text-xs text-gray-500">
                    {parent.niveau_fidelite}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activités populaires */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Activités les plus populaires
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={activitesPopulaires}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="nom_activite"
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="participants" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Participation par classe */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Participation par classe
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={participationParClasse}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="classe" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total_points" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Répartition par type d'activité */}
      {participationMensuelle &&
        participationMensuelle.repartition_par_type && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Répartition par type d'activité -{" "}
              {new Date(0, selectedMonth - 1).toLocaleString("fr", {
                month: "long",
              })}{" "}
              {selectedYear}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(
                      participationMensuelle.repartition_par_type
                    ).map(([type, count]) => ({
                      name: type,
                      value: count,
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(
                      participationMensuelle.repartition_par_type
                    ).map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Statistiques du mois
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total tampons:</span>
                      <span className="font-medium">
                        {participationMensuelle.total_tampons}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total activités:</span>
                      <span className="font-medium">
                        {participationMensuelle.total_activites}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Parents participants:</span>
                      <span className="font-medium">
                        {participationMensuelle.parents_participants}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Points attribués:</span>
                      <span className="font-medium">
                        {participationMensuelle.total_points_attribues}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Statistiques des récompenses */}
      {statsRecompenses && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Statistiques des récompenses
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600">
                {statsRecompenses.total_attribuees}
              </p>
              <p className="text-sm text-gray-500">Récompenses attribuées</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-success-600">
                {statsRecompenses.total_telechargees}
              </p>
              <p className="text-sm text-gray-500">Récompenses téléchargées</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-warning-600">
                {Math.round(statsRecompenses.taux_telechargement)}%
              </p>
              <p className="text-sm text-gray-500">Taux de téléchargement</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistiques;
